import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { formatPercent } from '../../lib/format.ts';
import { DATA_SLOT_BG } from './PulseChart.tsx';
import type { DataSlot } from './PulseChart.tsx';

export interface BarListItem {
  key: string;
  label: ReactNode;
  value: number;
  /** Folded remainder ("Other"): drawn grey, never a series colour. */
  other?: boolean;
  /** Something small before the label: a project tile, a swatch. */
  lead?: ReactNode;
}

export interface BarListProps {
  /** Largest first (the list does not sort). */
  items: readonly BarListItem[];
  format: (n: number) => string;
  /** One slot for every bar: the bars compare items of one kind. */
  slot?: DataSlot;
  /** Per item slot instead (a model mix whose colours match another chart on the page). */
  slotOf?: (item: BarListItem, index: number) => DataSlot;
  /** Show each item's share of the total after its value. */
  shares?: boolean;
  /** Accessible name: "Spend by project, this month". */
  label: string;
  className?: string;
}

/**
 * A ranked list with a bar under each label (data-viz.md: items compare as horizontal bars,
 * sorted by value). The value sits right-aligned on the label's line, the bar runs the full
 * width under it and grows from the start once; later changes ease its scale, never its width.
 */
export function BarList({ items, format, slot = 1, slotOf, shares, label, className }: BarListProps) {
  const max = Math.max(0, ...items.map((i) => i.value));
  const total = items.reduce((n, i) => n + i.value, 0);
  return (
    <ul aria-label={label} className={cn('flex min-w-0 flex-col gap-4', className)}>
      {items.map((item, i) => {
        const share = max > 0 ? item.value / max : 0;
        const ofTotal = total > 0 ? item.value / total : 0;
        // Under half a percent the bar would be an empty track that reads as broken: keep the row's
        // rhythm, draw no bar, and say "<1%" instead of a 0% that is not true.
        const trace = ofTotal < 0.005;
        const fill = item.other ? 'bg-fg-subtle' : DATA_SLOT_BG[slotOf ? slotOf(item, i) : slot];
        return (
          <li key={item.key} className="motion-arrive flex min-w-0 flex-col gap-2">
            <span className="flex min-w-0 items-baseline gap-3">
              {item.lead != null && <span className="flex shrink-0 self-center">{item.lead}</span>}
              <span className={cn('min-w-0 flex-1 truncate text-ui', item.other ? 'text-fg-muted' : 'text-fg')}>{item.label}</span>
              <span className="shrink-0 text-ui font-medium text-fg num">{format(item.value)}</span>
              {shares && <span className="w-10 shrink-0 text-right text-caption text-fg-subtle num">{item.value > 0 && trace ? '<1%' : formatPercent(ofTotal)}</span>}
            </span>
            {trace ? (
              <span aria-hidden className="h-1.5" />
            ) : (
              <span aria-hidden className="relative h-1.5 overflow-hidden rounded-pill bg-active">
                <span className={cn('motion-grow-x absolute inset-0 rounded-pill', fill)} style={{ scale: `${share} 1`, transitionDelay: `${i * 40}ms` }} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
